import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath, localizedPath } from '../../i18n';
import patientForms from '../../data/forms/patient-forms.json';

function StateBadge({ lang }) {
  const colors = {
    en: { bg: 'rgba(59,130,246,0.15)', color: '#1d4ed8', label: 'EN' },
    es: { bg: 'rgba(234,88,12,0.15)', color: '#c2410c', label: 'ES' },
  };
  const style = colors[lang] || colors.en;
  return (
    <span style={{
      background: style.bg,
      color: style.color,
      border: `1px solid ${style.color}40`,
      borderRadius: 6,
      padding: '2px 8px',
      fontSize: '0.78rem',
      fontWeight: 700,
      letterSpacing: '0.05em',
    }}>
      {style.label}
    </span>
  );
}

function LocationBadge({ name }) {
  const isNC = name && name.includes('North Carolina');
  const isSC = name && name.includes('South Carolina');
  const label = isNC ? 'NC' : isSC ? 'SC' : '';
  if (!label) return null;
  return (
    <span style={{
      background: 'rgba(20,184,166,0.15)',
      color: '#0f766e',
      border: '1px solid rgba(20,184,166,0.3)',
      borderRadius: 6,
      padding: '2px 8px',
      fontSize: '0.78rem',
      fontWeight: 700,
      letterSpacing: '0.05em',
    }}>
      {label}
    </span>
  );
}

export default function NewPatientForms() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;
  const brandKey = brand.brandKey || 'gso';

  const isApexNC = brandKey === 'apex-nc';

  return (
    <div className="new-patient-page">
      <section className="glass-dark" style={{ padding: '60px 0', textAlign: 'center', borderRadius: 0, marginBottom: 40 }}>
        <div className="container">
          <h1>{t('forms.title', locale)}</h1>
          <p style={{ opacity: 0.9 }}>{brand.name}</p>
        </div>
      </section>

      <section className="container" style={{ maxWidth: 860, margin: '0 auto', padding: '0 20px 60px' }}>
        {isApexNC ? (
          <>
            <div className="glass-panel" style={{ padding: 24, marginBottom: 32 }}>
              <h2 style={{ marginBottom: 8 }}>{t('forms.completeOnline', locale)}</h2>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.7 }}>
                {t('forms.completeOnlineDesc', locale)}
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
              {patientForms.map((form, i) => (
                <div
                  key={i}
                  className="glass-card"
                  style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}
                >
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <StateBadge lang={form.language} />
                    <LocationBadge name={form.name} />
                  </div>
                  <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--color-primary)' }}>
                    {form.label || form.name}
                  </h3>
                  <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: '0.88rem', lineHeight: 1.6 }}>
                    {form.name}
                  </p>
                  {form.note && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-secondary)', opacity: 0.75, fontStyle: 'italic' }}>
                      {form.note}
                    </p>
                  )}
                  <a
                    href={form.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="glass-btn glass-btn-primary"
                    style={{ textAlign: 'center', marginTop: 'auto' }}
                  >
                    {t('forms.fillOut', locale)}
                  </a>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="glass-panel" style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: 16 }}>📋</div>
            <h2 style={{ marginBottom: 12 }}>{t('forms.patientForms', locale)}</h2>
            <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.8, marginBottom: 24 }}>
              {t('forms.contactDesc', locale)}
            </p>
            <Link to={localizedPath('/contact', locale)} className="glass-btn glass-btn-primary">
              {t('forms.contactCta', locale)}
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
